import React, { useState, useEffect } from 'react';
import { Icon } from '../icons/Icon';
import Avatar from '../Avatar';
import type { IntlShape } from 'react-intl';
import { CORE_CONTROL_GHOST } from '../../constants/corePublicTheme';
import { trackEvent } from '../../utils/analytics/analytics';

export interface UserMenuProps {
  intl: IntlShape;
  user?: {
    image?: string;
    picture?: string;
    name?: string;
  } | null;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  className?: string;
}

/**
 * User menu button component for navigation
 * Opens the mobile menu drawer with user options.
 * Defers showing user avatar until after mount so server and client both render the same placeholder (Avatar with no src), avoiding hydration mismatch when user differs between server and client.
 */
export const UserMenu: React.FC<UserMenuProps> = ({
  intl,
  user,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  className = '',
}) => {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const handleClick = () => {
    const willOpen = !(isMobileMenuOpen ?? false);
    if (willOpen) {
      trackEvent({ name: 'nav_menu_open', params: { menu: 'user', locale: intl.locale } });
    }
    setIsMobileMenuOpen?.(willOpen);
  };

  // Only use user avatar after mount so SSR and first client paint both render Avatar with no src (initials "?") — avoids hydration mismatch.
  const effectiveUser = hasMounted ? user : null;
  const avatarUrl =
    effectiveUser &&
    (effectiveUser.image ??
      effectiveUser.picture ??
      (effectiveUser as { avatar?: string })?.avatar ??
      (effectiveUser as { avatar_url?: string })?.avatar_url);
  const hasUser = !!effectiveUser;

  return (
    <button
      suppressHydrationWarning
      className={`flex items-center gap-2 py-1.5 rounded-full cursor-pointer ${CORE_CONTROL_GHOST} ${
        hasUser ? 'pl-1 pr-2' : 'px-2'
      } ${className}`}
      onClick={handleClick}
      aria-label={intl.formatMessage({
        id: 'navigation.menu',
        defaultMessage: 'Menu',
        description: 'Button aria-label to open navigation menu',
      })}
    >
      {/* Always render Avatar so DOM structure is identical on server and client; pass no src until after mount. */}
      <span className="flex items-center gap-2 flex-shrink-0">
        <span className="flex-shrink-0 overflow-hidden rounded-full w-8 h-8 flex items-center justify-center">
          <Avatar
            src={avatarUrl ?? null}
            size={32}
            name={effectiveUser?.name || 'User'}
          />
        </span>
        <span className="text-[#3D3529] dark:text-[#C4A882] flex-shrink-0 inline-flex items-center">
          <Icon name="bars3" useMaskForColor className="w-5 h-5" />
        </span>
      </span>
    </button>
  );
};
